import { getCountryName } from "@/features/flight-estimator/_logic/iso-country-utils";

/** The airport fields the ranking reads. */
export interface AirportCandidate {
  iata_code: string | null;
  type: string;
  municipality: string | null;
  iso_country: string;
}

/**
 * Main airport for cities that several airports serve, where name and country cannot choose
 * (London is Heathrow, Stansted, Gatwick, Luton and City). Keyed by lowercased city name.
 */
export const PRIMARY_AIRPORTS: Readonly<Record<string, string>> = {
  london: "LHR",
  "new york": "JFK",
  "new york city": "JFK",
  paris: "CDG",
  tokyo: "HND",
  chicago: "ORD",
  washington: "IAD",
  "washington dc": "IAD",
  milan: "MXP",
  rome: "FCO",
  istanbul: "IST",
  "sao paulo": "GRU",
  "são paulo": "GRU",
  "rio de janeiro": "GIG",
  "buenos aires": "EZE",
  "mexico city": "MEX",
  toronto: "YYZ",
  montreal: "YUL",
  houston: "IAH",
  dallas: "DFW",
  "los angeles": "LAX",
  "san francisco": "SFO",
  berlin: "BER",
  stockholm: "ARN",
  oslo: "OSL",
  moscow: "SVO",
  dubai: "DXB",
  bangkok: "BKK",
  shanghai: "PVG",
  beijing: "PEK",
  seoul: "ICN",
  osaka: "KIX",
  taipei: "TPE",
  jakarta: "CGK",
  manila: "MNL",
};

/** Constituent countries our venue data names instead of the United Kingdom. */
const GB_ALIASES = new Set(["england", "scotland", "wales", "northern ireland", "uk", "united kingdom"]);

/**
 * Whether an airport's country matches a free-text country name or code.
 * @param isoCountry - The airport's ISO alpha-2 country.
 * @param hint - A country as people or our venue data write it ("Spain", "Scotland", "ES").
 * @returns True on a match.
 * @throws Never.
 */
export const countryMatches = (isoCountry: string, hint: string): boolean => {
  const normalized = hint.trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === isoCountry.toLowerCase()) return true;
  if (isoCountry === "GB" && GB_ALIASES.has(normalized)) return true;
  return getCountryName(isoCountry)?.toLowerCase() === normalized;
};

/**
 * Picks the airport for a city from search candidates (in the search's own relevance order).
 *
 * Name similarity alone picks the wrong city when names repeat: "Barcelona" ranks Barcelona,
 * Venezuela first. So an exact city match wins, then the country hint, then a large airport.
 *
 * @param candidates - Airports from `search_airports`, best match first.
 * @param city - The city as typed, without any country part.
 * @param countryHint - A country to prefer, or null.
 * @returns The chosen IATA code, or null when no candidate has one.
 * @throws Never.
 */
export const pickAirport = (
  candidates: AirportCandidate[],
  city: string,
  countryHint: string | null
): string | null => {
  const target = city.trim().toLowerCase();
  const primary = PRIMARY_AIRPORTS[target];
  const primaryAirport = candidates.find((airport) => airport.iata_code === primary);
  if (primaryAirport && (!countryHint || countryMatches(primaryAirport.iso_country, countryHint))) return primary;
  const score = (airport: AirportCandidate): number =>
    (airport.municipality?.trim().toLowerCase() === target ? 4 : 0) +
    (countryHint && countryMatches(airport.iso_country, countryHint) ? 2 : 0) +
    (airport.type === "large_airport" ? 1 : 0);
  const ranked = candidates
    .filter((airport) => airport.iata_code)
    .map((airport, index) => ({ airport, index, score: score(airport) }))
    // Exact-city ties have equal relevance, so break them by code to resolve the same way every time.
    .sort(
      (a, b) =>
        b.score - a.score ||
        (b.score >= 4 ? 0 : a.index - b.index) ||
        a.airport.iata_code!.localeCompare(b.airport.iata_code!)
    );
  return ranked[0]?.airport.iata_code ?? null;
};
